'use client';

import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import Image from 'next/image';
import Link from 'next/link';
import { Trophy, DollarSign, Users } from 'lucide-react';

export default function Features() {
  const [currencySymbol, setCurrencySymbol] = useState('€');

  useEffect(() => {
    const detectCurrency = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        if (data.currency === 'GBP') {
          setCurrencySymbol('£');
        } else {
          setCurrencySymbol('€');
        }
      } catch (error) {
        setCurrencySymbol('€'); // Silent fallback to Euro
      }
    };
    detectCurrency();
  }, []);

  return (
    <section className="bg-[#0a0a0a] py-24 px-6 md:px-12 lg:px-24">
      <div className="mx-auto w-full max-w-7xl">
        
        {/* Social Proof Stats */}
        <div className="mb-20 grid gap-6 md:grid-cols-3">
          {[
            { label: 'REGISTERED ORGANIZERS', value: '1400+' },
            { label: 'MANAGED COMPETITIONS', value: '10,000+' },
            { label: 'MONTHLY REVENUE', value: `+500${currencySymbol}` },
          ].map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="group relative overflow-hidden rounded-2xl border border-zinc-800 bg-[#141414] p-8 text-center shadow-2xl transition-all hover:border-[#b4ff00]/30"
            >
              <div className="relative z-10 mb-2 text-4xl font-black tracking-tighter text-[#b4ff00] md:text-5xl">
                {stat.value}
              </div>
              <div className="relative z-10 text-[10px] font-bold tracking-[0.2em] text-zinc-500 uppercase">
                {stat.label}
              </div>
              <div className="absolute inset-0 -z-0 bg-gradient-to-b from-white/5 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
            </motion.div>
          ))}
        </div>

        <div className="grid w-full overflow-hidden gap-6 md:grid-cols-3">
          
          {/* Competitions Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="group relative overflow-hidden rounded-2xl border border-zinc-800 bg-[#141414] p-8 md:col-span-2"
          >
            <div className="relative z-10 flex h-full flex-col justify-between md:w-1/2">
              <div>
                <div className="mb-6 flex h-10 w-10 items-center justify-center rounded-full bg-[#b4ff00]/10 text-[#b4ff00]">
                  <Trophy size={20} />
                </div>
                <h3 className="mb-4 text-2xl font-black tracking-tight text-white">COMPETITIONS</h3>
                <p className="text-sm text-zinc-400">
                  Scalable league engines and tournament structures designed for automated bracket management and real-time live data feeds.
                </p>
              </div>
              <Link 
                href="https://disporty.com/en/explore/competitions" 
                className="mt-8 flex items-center gap-2 text-sm font-bold text-[#b4ff00] transition-colors hover:text-white"
              >
                Explore Competitions <span className="text-lg">→</span>
              </Link>
            </div>
            
            <div className="absolute inset-y-0 right-0 w-1/2 opacity-40 transition-opacity duration-500 group-hover:opacity-60 hidden md:block">
              <div className="absolute inset-0 bg-gradient-to-r from-[#141414] to-transparent z-10" />
              <Image
                src="https://images.unsplash.com/photo-1489944440615-453fc2b6a9a9?q=80&w=1000&auto=format&fit=crop"
                alt="Stadium"
                fill
                className="object-cover"
                referrerPolicy="no-referrer"
              />
            </div>
          </motion.div>

          {/* Commercial Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex flex-col justify-between rounded-2xl border border-zinc-800 bg-[#141414] p-8 md:col-span-1"
          >
            <div>
              <div className="mb-6 flex h-10 w-10 items-center justify-center rounded-full bg-[#b4ff00]/10 text-[#b4ff00]">
                <DollarSign size={20} />
              </div>
              <h3 className="mb-4 text-2xl font-black tracking-tight text-white">COMMERCIAL</h3>
              <p className="text-sm text-zinc-400">
                Turn player data and match broadcasts into revenue streams with native sponsor integration and digital ticketing.
              </p>
            </div>
            
            <div className="mt-8 rounded-lg bg-[#0a0a0a] p-4">
              <div className="mb-2 flex items-center justify-between text-xs font-bold text-zinc-500">
                <span>REV-GROWTH</span>
                <span className="text-[#b4ff00] text-lg">+214%</span>
              </div>
              <div className="h-1 w-full bg-zinc-800 rounded-full overflow-hidden">
                <motion.div 
                  initial={{ width: 0 }}
                  whileInView={{ width: '75%' }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.5 }}
                  className="h-full bg-[#b4ff00]" 
                />
              </div>
            </div>
          </motion.div>

          {/* Community Hubs Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex w-full flex-col overflow-hidden justify-between gap-4 md:gap-8 rounded-2xl border border-zinc-800 bg-[#141414] p-4 md:p-8 md:col-span-3 md:flex-row md:items-center"
          >
            <div className="md:w-1/2">
              <div className="mb-6 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white">
                <Users size={20} />
              </div>
              <h3 className="mb-4 text-2xl font-black tracking-tight text-white">COMMUNITY HUBS</h3>
              <p className="mb-6 text-sm text-zinc-400">
                The social layer of sports. Verified player profiles, achievement badges, and federation-wide social feeds that keep fans engaged 24/7.
              </p>
              <div className="flex items-center gap-4">
                <div className="flex -space-x-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-8 w-8 rounded-full border-2 border-[#141414] bg-zinc-700 flex items-center justify-center text-[10px] font-bold text-white">
                      P{i}
                    </div>
                  ))}
                </div>
                <span className="text-xs font-medium text-zinc-500">+12k Players joined today</span>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4 md:w-1/2 md:justify-end">
              <div className="flex flex-col justify-center rounded-2xl bg-[#0a0a0a] p-8 md:w-56 md:h-48 shadow-xl border border-zinc-800/50">
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-[#b4ff00]/10 text-[#b4ff00]">
                  <Users size={20} />
                </div>
                <div className="text-[10px] font-bold tracking-widest text-zinc-500 uppercase">Profile</div>
                <div className="text-lg font-bold text-white">EA account connection</div>
              </div>
              <div className="flex flex-col justify-center rounded-2xl bg-[#0a0a0a] p-8 md:w-56 md:h-48 shadow-xl border border-zinc-800/50">
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-[#b4ff00]/10 text-[#b4ff00]">
                   <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.419 0 1.334-.956 2.419-2.157 2.419zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.419 0 1.334-.946 2.419-2.157 2.419z" /></svg>
                </div>
                <div className="text-[10px] font-bold tracking-widest text-zinc-500 uppercase">Social</div>
                <div className="text-lg font-bold text-white">Discord Integration</div>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}
